import React, { useEffect, useState } from 'react';
import PageContainer from '../../../../components/PageContainer';
import { Button, CircularProgress, Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Add, ArrowBack } from '@mui/icons-material';
import TextInput from '../../../../components/common/TextInput';
import SelectInput from '../../../../components/common/SelectInput';
import { Link, useHistory, useParams } from 'react-router-dom';
import appRoutes from '../../../../routes/routeConfig';
import { createExternalUser, editUser, getRoles, getUserById, getUserRoles } from '../../../../redux/api/userMgt';
import { useSnackbar } from 'notistack';
import { getAllInstitutions } from '../../../../redux/api/institution';

const ExternalUserCreate = () => {
    const history = useHistory();
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const formFields = {
        username: '',
        password: '',
        confirmPassword: '',
        lastName: '',
        middleName: '',
        firstName: '',
        employeeId: '',
        email: '',
    };
    const [user, setUser] = useState(formFields);
    const [institutions, setInstitutions] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const getUserInfo = async (userId) => {
        try {
            setProcessing(true);
            const response = await getUserById(userId);
            const resp = await getUserRoles(userId);
            setUser({
                ...response,
                userRoles: resp,
            });
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const init = async (userId) => {
        try {
            const res = await getAllInstitutions();
            setInstitutions(res?.resultObject || []);
            if (userId) {
                const resp = await getUserRoles(userId);
                setUser({
                    ...user,
                    userRoles: resp,
                });
            }
        } catch (err) {
            console.log(err);
        }
    };

    const submitForm = async (values, { resetForm }) => {
        try {
            let resp;
            if (isEditing) {
                resp = await editUser(values);
            } else {
                resp = await createExternalUser(values);
            }
            if (resp.isSuccess) {
                enqueueSnackbar(isEditing ? 'User Updated Successfully' : 'User Created Successfully', { variant: 'success' });
                history.goBack();
                resetForm();
            } else {
                enqueueSnackbar(resp?.message ?? 'An error occurred', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    useEffect(() => {
        init();

        if (params?.userId) {
            setIsEditing(true);
            getUserInfo(params?.userId);
        } else {
            setUser(formFields);
            setIsEditing(false);
        }
    }, [params]);

    return (
        <PageContainer
            pageTitle="Create External User"
            tagline=""
            processing={processing}
            buttonComponent={
                <>
                    <Link to={appRoutes.internal.users.external}>
                        <Button startIcon={<ArrowBack />} variant="outlined">
                            Go Back
                        </Button>
                    </Link>
                </>
            }
        >
            <div className="paper">
                <Formik
                    initialValues={{ ...user }}
                    enableReinitialize
                    onSubmit={submitForm}
                    validationSchema={Yup.object().shape({
                        lastName: Yup.string().required('Last Name is required.'),
                        firstName: Yup.string().required('First Name is required.'),
                        email: Yup.string().email('Value must be a valid email address').required('Email is required.'),
                        employeeId: Yup.string().required('Employee Id is required.'),
                    })}
                >
                    {({ isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <Grid container spacing={3} paddingBottom={3}>
                                <Grid item xs={12} md={6} lg={4}>
                                    <TextInput id="firstName" required label="First Name" />
                                </Grid>

                                <Grid item xs={12} md={6} lg={4}>
                                    <TextInput id="lastName" required label="Last name" />
                                </Grid>
                                <Grid item xs={12} md={6} lg={4}>
                                    <TextInput id="email" required label="Email" type="email" />
                                </Grid>
                                <Grid item xs={12} md={6} lg={4}>
                                    <TextInput id="employeeId" required label="Employee ID" />
                                </Grid>
                                <Grid item xs={12} md={6} lg={4}>
                                    <TextInput id="phoneNumber" label="Phone Number" />
                                </Grid>
                                <Grid item xs={12} md={6} lg={4}>
                                    <SelectInput
                                        id="institutionId"
                                        label="Institution"
                                        required
                                        options={institutions}
                                        field="institutionId"
                                        fieldDisplay="name"
                                        customChange={(_f, _v, record) => {
                                            setFieldValue('institutionName', record?.name);
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6} lg={4} container justifyContent={'flex-end'} flexDirection="column">
                                    <Button disabled={isSubmitting} startIcon={!isEditing && <Add />} fullWidth variant="contained" type="submit" style={{ marginTop: '.5rem' }}>
                                        {isSubmitting ? <CircularProgress size={15} color="inherit" /> : isEditing ? 'Edit User' : 'Add User'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </div>
        </PageContainer>
    );
};

export default ExternalUserCreate;
